// frontend/src/components/Layout.js
import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import api from '../api';

const Layout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const location = useLocation();
  const navigate = useNavigate();

  // ---- Detect mobile screen (<= 768px) ----
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // ---- On mobile, start closed; on desktop, always open ----
  useEffect(() => {
    if (isMobile) setSidebarOpen(false);
    else setSidebarOpen(true);
  }, [isMobile]);

  // ---- Close dropdowns when clicking outside ----
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest('.notif-wrapper')) setNotifOpen(false);
      if (!e.target.closest('.profile-wrapper')) setProfileOpen(false);
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  // Read user from localStorage
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdmin = user.role === 'admin';

  // ---- Fetch real notifications ----
  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const res = await api.get('/api/assets/transactions');
        const txs = res.data || [];
        const recent = txs.slice(0, 5).map(tx => ({
          id: tx.id,
          message: `${tx.type === 'deposit' ? 'New deposit' : 'Release'} of ${tx.amount} ${tx.currency || 'ETH'}`,
          time: new Date(tx.createdAt).toLocaleString(),
        }));
        setNotifications(recent);
        setUnreadCount(recent.length);
      } catch (err) {
        console.error('Could not fetch notifications', err);
        setNotifications([]);
        setUnreadCount(0);
      }
    };
    fetchNotifications();
  }, [user.id]);

  // ✅ Updated menu items for admin
  const menuItems = [
    // Admin gets dedicated dashboard link
    ...(isAdmin
      ? [{ path: '/admin/dashboard', label: 'Dashboard', icon: '📊' }]
      : [{ path: '/dashboard', label: 'Dashboard', icon: '📊' }]
    ),
    { path: '/vault', label: 'Asset Vault', icon: '🏦' },
    { path: '/send', label: 'Send', icon: '✈️' },
    { path: '/vault-overview', label: 'Vault Overview', icon: '📈' },
    { path: '/releases', label: 'Release Management', icon: '🔓' },
    { path: '/cases', label: 'Active Cases', icon: '⚖️' },
    { path: '/transactions', label: 'Transactions', icon: '📜' },
    { path: '/verification', label: 'Verification Center', icon: '🆔' },
    { path: '/security-center', label: 'Security Center', icon: '🛡️' },
    { path: '/reports', label: 'Reports', icon: '📋' },
    { path: '/support', label: 'Support Center', icon: '🎫' },
    // Admin-only items
    ...(isAdmin
      ? [
          { path: '/admin/users', label: 'User Management', icon: '👥' },
          { path: '/admin/kyc', label: 'KYC Management', icon: '📋' },
          { path: '/admin/tickets', label: 'Support Tickets', icon: '🎫' },
          { path: '/admin/logs', label: 'Audit Logs', icon: '📄' },
          { path: '/admin/withdrawals', label: 'Withdrawals', icon: '💸' },
        ]
      : []),
    { path: '/settings', label: 'Settings', icon: '⚙️' },
  ];

  // ✅ Logout: redirect admin to admin login, normal users to standard login
  const logout = () => {
    const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate(currentUser.role === 'admin' ? '/admin-login' : '/login');
  };

  // ---- When a link is clicked on mobile, close the sidebar ----
  const handleMobileNav = () => {
    if (isMobile) setSidebarOpen(false);
  };

  return (
    <div className="app-shell">
      {/* ---- SIDEBAR ---- */}
      <aside className={`push-sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-inner">
          <div className="sidebar-brand">
            <span className="vault-icon-bold">🔒</span> RECOVERY VAULT
          </div>
          <nav className="sidebar-nav">
            {menuItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`sidebar-nav-link ${location.pathname === item.path ? 'active' : ''}`}
                onClick={handleMobileNav}
              >
                <span className="icon">{item.icon}</span>
                <span>{item.label}</span>
              </Link>
            ))}
            <button onClick={logout} className="sidebar-nav-link" style={{ width: '100%', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer' }}>
              <span className="icon">🚪</span>
              <span>Logout</span>
            </button>
          </nav>
        </div>
      </aside>

      {/* ---- MOBILE OVERLAY (closes sidebar) ---- */}
      {isMobile && sidebarOpen && (
        <div className="sidebar-overlay" onClick={() => setSidebarOpen(false)} />
      )}

      {/* ---- MAIN AREA ---- */}
      <div className="main-area">
        <header className="push-topbar">
          <div className="topbar-left">
            <button className="hamburger-btn" onClick={() => setSidebarOpen(!sidebarOpen)}>
              <span className="hamburger-line"></span>
              <span className="hamburger-line"></span>
              <span className="hamburger-line"></span>
            </button>
            <div className="topbar-logo">Recovery Vault</div>
          </div>

          <div className="topbar-right">
            {/* Notification bell */}
            <div className="notif-wrapper">
              <button className="notif-bell" onClick={() => setNotifOpen(!notifOpen)}>
                🔔
                {unreadCount > 0 && <span className="notif-badge">{unreadCount}</span>}
              </button>
              {notifOpen && (
                <div className="notif-dropdown">
                  <h4>Notifications</h4>
                  {notifications.length === 0 ? (
                    <div className="notif-empty">No new notifications</div>
                  ) : (
                    <>
                      <ul className="notif-list">
                        {notifications.map((n) => (
                          <li key={n.id} className="notif-item">
                            <p className="notif-msg">{n.message}</p>
                            <span className="notif-time">{n.time}</span>
                          </li>
                        ))}
                      </ul>
                      <button className="notif-clear" onClick={() => setUnreadCount(0)}>
                        Mark all as read
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* User avatar */}
            <div className="profile-wrapper">
              <button className="profile-avatar" onClick={() => setProfileOpen(!profileOpen)}>
                {user.name ? user.name.charAt(0).toUpperCase() : 'U'}
              </button>
              {profileOpen && (
                <div className="profile-dropdown">
                  <div className="profile-name">{user.name || 'User'}</div>
                  <div className="profile-email" style={{ fontSize: '0.8rem', padding: '0 1rem 0.5rem', color: '#64748b' }}>
                    {user.email || ''}
                  </div>
                  <hr />
                  <Link to="/settings">Settings</Link>
                  <button onClick={logout}>Logout</button>
                </div>
              )}
            </div>
          </div>
        </header>
        <div className="page-content">{children}</div>
      </div>
    </div>
  );
};

export default Layout;
